import { useState, useCallback, useMemo, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageContext from './LanguageContext';
import useContextUser from 'hooks/contexts/useContextUser';
import { IChildrenProps } from 'interfaces/general.interface';
import { getLanguagesAPI } from 'api/endpoints/general/general.api';
import { useAuth0 } from '@auth0/auth0-react';
import { TLanguageResponseItem } from 'api/endpoints/general/general.response.interface';
import useContextGeneral from 'hooks/contexts/useContextGeneral';

export type TLangType = 'en-gb' | 'it' | 'fr' | 'es';
export const langs: { [key: string]: TLangType } = {
  es: 'es',
  en: 'en-gb',
  fr: 'fr',
  it: 'it',
};

function LanguageProvider({ children }: IChildrenProps) {
  const { i18n } = useTranslation();
  const { dbUser } = useContextUser();
  const { ping } = useContextGeneral();
  const [language, setLanguage] = useState('es' as TLangType);
  const [languages, setLanguages] = useState([] as TLanguageResponseItem[]);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  useEffect(() => {
    (async () => {
      try {
        if (!isAuthenticated || !ping) return;
        const token = await getAccessTokenSilently();
        if (!token) return;
        const languagesFetch = await getLanguagesAPI({ token });
        if (languagesFetch.response.status === 200) {
          setLanguages(languagesFetch.data);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  }, [isAuthenticated, ping, getAccessTokenSilently]);

  const setLanguageAction = useCallback(
    async (language: TLangType) => {
      document.documentElement.setAttribute('lang', language);
      setLanguage(language);
      i18n.changeLanguage(language);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dbUser?.id]
  );

  const memorizedProviderLanguage = useMemo(
    () => ({
      language,
      languages,
      setLanguageAction,
    }),
    [language, languages, setLanguageAction]
  );

  return (
    <LanguageContext.Provider value={memorizedProviderLanguage}>
      {children}
    </LanguageContext.Provider>
  );
}

export default memo(LanguageProvider);
